import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import './TrialCredentialsModal.css';

const TrialCredentialsModal = ({ 
  isOpen, 
  onClose, 
  account1, 
  account2, 
  createdGroups,
  orgName 
}) => {
  const [copiedField, setCopiedField] = useState(null);
  const intl = useIntl();

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000); // Clear the "copied" state after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const copyAllCredentials = async () => {
    let credentialsText = `
${intl.formatMessage({ id: 'TRIAL_CREDENTIALS_HEADER' }, { orgName })}
${intl.formatMessage({ id: 'TRIAL_CREDENTIALS_SEPARATOR' })}

${intl.formatMessage({ id: 'TRIAL_ACCOUNT_1_LABEL' })}
${intl.formatMessage({ id: 'TRIAL_EMAIL_LABEL' }, { email: account1.email })}
${intl.formatMessage({ id: 'TRIAL_PASSWORD_LABEL' }, { password: account1.password })}`;

    // Only include account2 if it exists
    if (account2) {
      credentialsText += `

${intl.formatMessage({ id: 'TRIAL_ACCOUNT_2_LABEL' })}
${intl.formatMessage({ id: 'TRIAL_EMAIL_LABEL' }, { email: account2.email })}
${intl.formatMessage({ id: 'TRIAL_PASSWORD_LABEL' }, { password: account2.password })}`;
    }

    credentialsText += `

${intl.formatMessage({ id: 'TRIAL_GENERATED_ON' }, { date: new Date().toLocaleString() })}`;

    await copyToClipboard(credentialsText.trim(), 'all');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="trial-credentials-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h4 className="modal-title">
            <i className="fas fa-check-circle text-success me-2"></i>
            <FormattedMessage id="TRIAL_SETUP_COMPLETE" defaultMessage="Trial Setup Complete!" />
          </h4>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>
        
        <div className="modal-body">
          <div className="credentials-section">
            <h5 className="section-title">
              <i className="fas fa-users me-2"></i>
              <FormattedMessage id="ACCOUNT_CREDENTIALS" defaultMessage="Account Credentials" />
            </h5>
            
            {/* Account 1 */}
            <div className="credential-card mb-3">
              <div className="card-header">
                <h6 className="mb-0">
                  <i className="fas fa-user me-2"></i>
                  <FormattedMessage id="ACCOUNT_1" defaultMessage="Account 1" />
                </h6>
              </div>
              <div className="card-body">
                <div className="credential-row">
                  <label><FormattedMessage id="EMAIL" defaultMessage="Email" /></label>
                  <div className="password-field">
                    <code className="password-code">{account1.email}</code>
                    <button 
                      className="btn btn-sm btn-outline-primary copy-btn"
                      onClick={() => copyToClipboard(account1.email, 'account1-email')}
                    >
                      {copiedField === 'account1-email' ? (
                        <><i className="fas fa-check"></i> <FormattedMessage id="COPIED" defaultMessage="Copied!" /></>
                      ) : (
                        <><i className="fas fa-copy"></i> <FormattedMessage id="COPY" defaultMessage="Copy" /></>
                      )}
                    </button>
                  </div>
                </div>
                <div className="credential-row">
                  <label><FormattedMessage id="PASSWORD" /></label>
                  <div className="password-field">
                    <code className="password-code">{account1.password}</code>
                    <button 
                      className="btn btn-sm btn-outline-primary copy-btn"
                      onClick={() => copyToClipboard(account1.password, 'account1-password')}
                    >
                      {copiedField === 'account1-password' ? (
                        <><i className="fas fa-check"></i> <FormattedMessage id="COPIED" defaultMessage="Copied!" /></>
                      ) : (
                        <><i className="fas fa-copy"></i> <FormattedMessage id="COPY" defaultMessage="Copy" /></>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Account 2 - Only show if account2 exists */}
            {account2 && (
              <div className="credential-card mb-3">
                <div className="card-header">
                  <h6 className="mb-0">
                    <i className="fas fa-user me-2"></i>
                    <FormattedMessage id="ACCOUNT_2" defaultMessage="Account 2" />
                  </h6>
                </div>
                <div className="card-body">
                  <div className="credential-row">
                    <label><FormattedMessage id="EMAIL" defaultMessage="Email" /></label>
                    <div className="password-field">
                      <code className="password-code">{account2.email}</code>
                      <button 
                        className="btn btn-sm btn-outline-primary copy-btn"
                        onClick={() => copyToClipboard(account2.email, 'account2-email')}
                      >
                        {copiedField === 'account2-email' ? (
                          <><i className="fas fa-check"></i> <FormattedMessage id="COPIED" defaultMessage="Copied!" /></>
                        ) : (
                          <><i className="fas fa-copy"></i> <FormattedMessage id="COPY" defaultMessage="Copy" /></>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="credential-row">
                    <label><FormattedMessage id="PASSWORD" defaultMessage="Password" /></label>
                    <div className="password-field">
                      <code className="password-code">{account2.password}</code>
                      <button 
                        className="btn btn-sm btn-outline-primary copy-btn"
                        onClick={() => copyToClipboard(account2.password, 'account2-password')}
                      >
                        {copiedField === 'account2-password' ? (
                          <><i className="fas fa-check"></i> <FormattedMessage id="COPIED" defaultMessage="Copied!" /></>
                        ) : (
                          <><i className="fas fa-copy"></i> <FormattedMessage id="COPY" defaultMessage="Copy" /></>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Created Groups */}
            <div className="groups-section mt-4">
              <h6>
                <i className="fas fa-layer-group me-2"></i>
                <FormattedMessage id="CREATED_GROUPS" defaultMessage="Created Groups" />
              </h6>
              <ul className="list-group list-group-flush">
                {createdGroups.map((group, index) => (
                  <li key={index} className="list-group-item d-flex align-items-center">
                    <i className="fas fa-check-circle text-success me-2"></i>
                    {group.name}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button 
            className="btn btn-primary me-2"
            onClick={copyAllCredentials}
          >
            {copiedField === 'all' ? (
              <><i className="fas fa-check"></i> <FormattedMessage id="ALL_CREDENTIALS_COPIED" defaultMessage="All Credentials Copied!" /></>
            ) : (
              <><i className="fas fa-copy"></i> <FormattedMessage id="COPY_ALL_CREDENTIALS" defaultMessage="Copy All Credentials" /></>
            )}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            <FormattedMessage id="CLOSE" defaultMessage="Close" />
          </button>
        </div>
        <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong><FormattedMessage id="IMPORTANT" defaultMessage="Important" /></strong>:
            {' '}
            <FormattedMessage 
              id="SAVE_CREDENTIALS_WARNING" 
              defaultMessage="Please save these credentials immediately. They will not be shown again for security reasons." 
            />
          </div>
      </div>
    </div>
  );
};

export default TrialCredentialsModal;